import { Button } from '@/shared/ui/button';
import { Eye, Edit3, Download, Layers3, PencilRuler } from 'lucide-react';

interface ModeControlsProps {
  mode: string;
  photoIndexMode: boolean;
  canEditPhotoMarkers: boolean;
  canEditAreaTags: boolean;
  hasAreaTags: boolean;
  setMode: (mode: 'view' | 'measure' | 'photoIndex' | 'areaTags') => void;
  setPhotoIndexMode: (value: boolean) => void;
  setShowMarkerToolbar: (value: boolean) => void;
  onDownload?: () => void;
  isDownloading?: boolean;
}

const ModeControls = ({
  mode,
  photoIndexMode,
  canEditPhotoMarkers,
  canEditAreaTags,
  hasAreaTags,
  setMode,
  setPhotoIndexMode,
  setShowMarkerToolbar,
  onDownload,
  isDownloading = false,
}: ModeControlsProps) => {
  const handlePhotoMarkerEditorClick = () => {
    if (photoIndexMode) {
      // Exit photo marker editor mode
      setPhotoIndexMode(false);
      setShowMarkerToolbar(false);
      setMode('view');
    } else {
      // Enter photo marker editor mode
      setPhotoIndexMode(true);
      setShowMarkerToolbar(true);
      setMode('view'); // Keep base mode as view
    }
  };

  const handleAreaTagsEditorClick = () => {
    if (!canEditAreaTags) return;
    if (mode === 'areaTags') {
      setMode('view');
    } else {
      setPhotoIndexMode(false);
      setShowMarkerToolbar(false);
      setMode('areaTags');
    }
  };

  const handleAreaTagSetClick = () => {
    if (!canEditAreaTags || !hasAreaTags) return;
    setPhotoIndexMode(false);
    setShowMarkerToolbar(false);
    setMode(mode === 'measure' ? 'view' : 'measure');
  };

  return (
    <div className="flex border rounded-lg overflow-hidden">
      <Button
        variant={mode === 'view' && !photoIndexMode ? 'default' : 'ghost'}
        size="sm"
        onClick={() => {
          setMode('view');
          if (photoIndexMode) {
            setPhotoIndexMode(false);
            setShowMarkerToolbar(false);
          }
        }}
        className="rounded-none border-r"
        title="View mode"
      >
        <Eye className="h-4 w-4" />
      </Button>
      {canEditPhotoMarkers && (
        <Button
          variant={photoIndexMode ? 'default' : 'ghost'}
          size="sm"
          onClick={handlePhotoMarkerEditorClick}
          className="rounded-none border-r"
          title="Photo marker editor"
        >
          <Edit3 className="h-4 w-4" />
        </Button>
      )}
      {canEditAreaTags && (
        <Button
          variant={mode === 'areaTags' ? 'default' : 'ghost'}
          size="sm"
          onClick={handleAreaTagsEditorClick}
          className="rounded-none border-r"
          title="Units/locations editor"
        >
          <PencilRuler className="h-4 w-4" />
        </Button>
      )}
      {canEditAreaTags && (
        <Button
          variant={mode === 'measure' ? 'default' : 'ghost'}
          size="sm"
          onClick={handleAreaTagSetClick}
          disabled={!hasAreaTags}
          className="rounded-none border-r"
          title={
            hasAreaTags ? 'Create phase/zone' : 'Create units/locations before making a phase/zone'
          }
        >
          <Layers3 className="h-4 w-4" />
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={onDownload}
        disabled={isDownloading}
        className="rounded-none"
        title="Download layout with markers"
      >
        <Download className={`h-4 w-4 ${isDownloading ? 'animate-pulse' : ''}`} />
      </Button>
    </div>
  );
};

export default ModeControls;
